<template>
  <organism-configuration-comparison
    :nodeOptions="nodeOptions"
    :baselineVersionOptions="baselineVersionOptions"
    :compareToVersionOptions="compareToVersionOptions"
    :selectedBaselineNode="selectedBaselineNode"
    :selectedBaselineNodeVersion="selectedBaselineNodeVersion"
    :selectedBaselineTimestamp="selectedBaselineTimestamp"
    :selectedBaselineConfigData="selectedBaselineConfigData"
    :selectedCompareToNode="selectedCompareToNode"
    :selectedCompareToNodeVersion="selectedCompareToNodeVersion"
    :selectedCompareToTimestamp="selectedCompareToTimestamp"
    :selectedCompareToConfigData="selectedCompareToConfigData"
    @onUpdateSelectedBaselineNode="onUpdateSelectedBaselineNode"
    @onUpdateSelectedBaselineNodeVersion="onUpdateSelectedBaselineNodeVersion"
    @onUpdateSelectedCompareToNode="onUpdateSelectedCompareToNode"
    @onUpdateSelectedCompareToNodeVersion="onUpdateSelectedCompareToNodeVersion"
  ></organism-configuration-comparison>
</template>

<script>
import OrganismConfigurationComparison from "@/components/organisms/networkConfigurationManagement/OrganismConfigurationComparison.vue"
import {
  getNodeList,
  getConfigVersionWithTimestamp,
  getNodeConfigurationDetail,
} from "@/api/api-module4"
import mixins from "@/mixins"
import mixinsDatetime from "@/mixins/mixinsDatetime"
export default {
  name: "ConfigurationComparison",
  components: {
    OrganismConfigurationComparison,
  },

  mixins: [mixins, mixinsDatetime],
  data() {
    return {
      nodeOptions: [],
      baselineVersionOptions: [],
      selectedBaselineNode: null,
      selectedBaselineTimestamp: "",
      selectedBaselineNodeVersion: null,
      selectedBaselineConfigData: "",

      compareToVersionOptions: [],
      selectedCompareToNode: null,
      selectedCompareToTimestamp: "",
      selectedCompareToNodeVersion: null,
      selectedCompareToConfigData: "",
    }
  },
  computed: {
    routeQueryParams() {
      return this.$route.query
    },
  },
  async mounted() {
    await this.getNodeOptionsFromApi()
    if (this.routeQueryParams) {
      this.mapSelectedNode()
      this.mapSelectedVersion()
      this.checkRouteParams()
    }
  },
  methods: {
    mapSelectedNode() {
      if (this.nodeOptions.length) {
        this.selectedBaselineNode = this.nodeOptions.find(
          (node) => node.nodeId === this.routeQueryParams.BaselineNodeId
        )
        this.selectedCompareToNode = this.nodeOptions.find(
          (node) => node.nodeId === this.routeQueryParams.CompareToNodeId
        )
      }
    },

    mapSelectedVersion() {
      this.selectedBaselineNodeVersion = {
        label: "",
        version: Number(this.routeQueryParams.BaselineVersion),
      }
      this.selectedCompareToNodeVersion = {
        label: "",
        version: Number(this.routeQueryParams.CompareToVersion),
      }
    },

    checkRouteParams() {
      if (
        this.routeQueryParams.BaselineNodeId &&
        this.routeQueryParams.BaselineVersion
      ) {
        this.getBaselineConfigurationFromApi()
      }
      if (
        this.routeQueryParams.CompareToNodeId &&
        this.routeQueryParams.CompareToVersion
      ) {
        this.getCompareToConfigurationFromApi()
      }
    },

    async getNodeOptionsFromApi() {
      this.showLoading(true)
      const filter = {
        PageSize: 100,
      }
      await getNodeList(filter)
        .then((response) => {
          if (response) {
            this.nodeOptions = response.data.map((node) => {
              return {
                nodeId: node.nodeId,
                nodeName: node.hostname,
                nodeValue: `${node.hostname} (${node.ipAddress})`,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getBaselineVersionOptionsFromApi(nodeId) {
      this.showLoading(true)
      await getConfigVersionWithTimestamp(nodeId)
        .then((response) => {
          if (response) {
            this.baselineVersionOptions = response.data.map((config) => {
              return {
                label: `Version ${config.version} (${this.setDateTimeFormat(
                  config.timestamp,
                  "dd/MM/yyyy HH:mm"
                )})`,
                version: config.version,
                timestamp: config.timestamp,
              }
            })
            this.selectedBaselineNodeVersion = this.baselineVersionOptions.find(
              (config) =>
                config.version === this.selectedBaselineNodeVersion.version
            )
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getCompareToVersionOptionsFromApi(nodeId) {
      this.showLoading(true)
      await getConfigVersionWithTimestamp(nodeId)
        .then((response) => {
          if (response) {
            this.compareToVersionOptions = response.data.map((config) => {
              return {
                label: `Version ${config.version} (${this.setDateTimeFormat(
                  config.timestamp,
                  "dd/MM/yyyy HH:mm"
                )})`,
                version: config.version,
                timestamp: config.timestamp,
              }
            })
            this.selectedCompareToNodeVersion = this.baselineVersionOptions.find(
              (config) =>
                config.version === this.selectedCompareToNodeVersion.version
            )
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getBaselineConfigurationFromApi() {
      this.showLoading(true)
      const nodeId = this.selectedBaselineNode.nodeId
      const version = this.selectedBaselineNodeVersion.version
      await getNodeConfigurationDetail(nodeId, version)
        .then((response) => {
          if (response) {
            this.selectedBaselineConfigData = response.data.configuration
            this.selectedBaselineTimestamp = response.data.timestamp
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getCompareToConfigurationFromApi() {
      this.showLoading(true)
      const nodeId = this.selectedCompareToNode.nodeId
      const version = this.selectedCompareToNodeVersion.version
      await getNodeConfigurationDetail(nodeId, version)
        .then((response) => {
          if (response) {
            this.selectedCompareToConfigData = response.data.configuration
            this.selectedCompareToTimestamp = response.data.timestamp
          }
        })
        .finally(() => this.showLoading(false))
    },

    onUpdateSelectedBaselineNode(selectedBaselineNode) {
      this.selectedBaselineNode = selectedBaselineNode
      const baselineNodeId = selectedBaselineNode.nodeId
      this.getBaselineVersionOptionsFromApi(baselineNodeId)
    },

    onUpdateSelectedBaselineNodeVersion(selectedBaselineNodeVersion) {
      const updatedVersion = selectedBaselineNodeVersion
      if (this.selectedBaselineNodeVersion !== updatedVersion) {
        this.selectedBaselineNodeVersion = updatedVersion
        this.getBaselineConfigurationFromApi()
      }
    },

    onUpdateSelectedCompareToNode(selectedCompareToNode) {
      this.selectedCompareToNode = selectedCompareToNode
      const compareToNodeId = selectedCompareToNode.nodeId
      this.getCompareToVersionOptionsFromApi(compareToNodeId)
    },

    onUpdateSelectedCompareToNodeVersion(selectedCompareToNodeVersion) {
      const updatedVersion = selectedCompareToNodeVersion
      if (this.selectedCompareToNodeVersion !== updatedVersion) {
        this.selectedCompareToNodeVersion = updatedVersion
        this.getCompareToConfigurationFromApi()
      }
    },

    // updateRouteQueryParams() {
    //   this.$router.push({
    //     query: {
    //       BaselineNodeId: this.selectedBaselineNode?.nodeId || undefined,
    //       BaselineVersion: this.selectedBaselineNodeVersion || undefined,
    //       CompareToNodeId: this.selectedCompareToNode?.nodeId || undefined,
    //       CompareToVersion: this.selectedCompareToNodeVersion || undefined,
    //     },
    //   })
    // },
  },
}
</script>

<style lang="scss" scoped></style>
